import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff, User, Lock } from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';

const topCompany = {
  name: 'PFC',
  logo: '/logo/pfs.png'
};

const pfsCompanies = [
  {
    name: 'Yummz Foods',
    logo: '/logo/yummz.png'
  },
  {
    name: 'Regal Fintech',
    logo: '/logo/rflogo2.svg'
  },
  {
    name: 'Swarn Sathi',
    logo: '/logo/swarnsathi.svg'
  },
  {
    name: 'Regal Fincorp',
    logo: '/logo/regalfincorp.png'
  }
];

const Login = () => {
  const [formData, setFormData] = useState({
    emailOrEmployeeId: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(
        String(formData.emailOrEmployeeId || '').trim(),
        formData.password,
      );
      if (!result.success) {
        setLoading(false);
      }
      // On success, AuthContext sets user and this route becomes <Navigate />; avoid setLoading after unmount.
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-amber-50 via-orange-50 to-lime-50 px-4 py-8 sm:px-6 lg:px-10" style={{ fontFamily: "'Manrope', 'Segoe UI', sans-serif" }}>
      <div className="pointer-events-none absolute -left-16 top-0 h-56 w-56 rounded-full bg-orange-300/30 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-72 w-72 rounded-full bg-emerald-300/30 blur-3xl" />

      <div className="relative mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-6xl items-center">
        <div className="grid w-full gap-6 lg:grid-cols-2">
          <section className="rounded-3xl border border-white/70 bg-white/75 p-6 shadow-xl backdrop-blur-md sm:p-8 lg:p-10">
            <h1 className="text-3xl font-extrabold leading-tight text-slate-900 sm:text-4xl">
              Welcome to HRMS
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">
              Start your day with everything in one place. Attendance, tasks, payroll and requests are ready for you as soon as you sign in.
            </p>

            <div className="mt-6 space-y-4">
              {/* PFS — big boss logo, centered */}
              <div className="flex justify-center">
                <img
                  src={topCompany.logo}
                  alt="PFS logo"
                  className="h-20 w-40 object-contain"
                />
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {pfsCompanies.map((company) => (
                  <div
                    key={company.name}
                    className="rounded-2xl border border-slate-200 bg-white px-3 py-3 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-12 w-20 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white p-2">
                        <img src={company.logo} alt={`${company.name} logo`} className="h-full w-full object-contain" />
                      </div>
                      <p className="text-sm font-semibold text-slate-800">{company.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl sm:p-8 lg:p-10 max-w-md">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">Sign in to continue</h2>
              <p className="mt-2 text-sm text-slate-500">
                Use your Email or Employee ID to access your workspace.
              </p>
            </div>
          
            <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="emailOrEmployeeId" className="mb-2 block text-sm font-semibold text-slate-700">
                  Email or Employee ID
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <User className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    id="emailOrEmployeeId"
                    name="emailOrEmployeeId"
                    type="text"
                    autoComplete="username"
                    required
                    className="block w-full rounded-xl border border-slate-300 bg-white px-3 py-3 pl-10 text-sm text-slate-900 outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-amber-200"
                    placeholder="eg : PFS0001"
                    value={formData.emailOrEmployeeId}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="mb-2 block text-sm font-semibold text-slate-700">
                  Password
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <Lock className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    className="block w-full rounded-xl border border-slate-300 bg-white px-3 py-3 pl-10 pr-11 text-sm text-slate-900 outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-amber-200"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <button
                      type="button"
                      className="text-slate-400 transition hover:text-slate-700 focus:outline-none"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="group relative flex w-full justify-center rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Login;
